import { getCommandingModeMenuItem } from '../commandBarItems/getCommandingModeMenuItem';
import { getHamburgerItem } from '../commandBarItems/getHamburgerItem';
import { getNewEventItem } from '../commandBarItems/getNewEventItem';
import { getBoardCommandBarConfig } from '../configs/getBoardCommandBarConfig';
import { getSurfaceCommandBarConfig } from '../configs/getSurfaceCommandBarConfig';
import { CalendarModuleCommandBarConfig } from '../schema';
import { observer } from 'mobx-react-lite';
import { getIsLeftNavShown, shouldShowLeftNavAsOverlay } from 'owa-calendar-layout';
import { isBoardViewTypeSelected } from 'owa-calendar-module-store';
import { commandBarEllipsisAriaLabel } from 'owa-calendar-shared-strings/lib/strings/calendarSurfaceCommandBar.locstring.json';
import { isFeatureEnabled } from 'owa-feature-flags';
import { FilterableCommandBar, FilterableMenuItem } from 'owa-filterable-menu';
import loc from 'owa-localize';
import * as React from 'react';

const styles = require('./CalendarModuleCommandBar.scss');
const classNames = require('classnames');

export default observer(function CalendarModuleCommandBar() {
    let config: CalendarModuleCommandBarConfig;

    const shouldShowHamburgerAndNewEventItems =
        isFeatureEnabled('tri-officeRail') &&
        (!getIsLeftNavShown() || shouldShowLeftNavAsOverlay());

    // Due to some weird behavior with how fabric handles ref arguments, we need pass thru this callback
    // to force rerender the module command bar when a command bar submenu state has been changed  (see bug #7582 in the Fluentui repo)
    const [keyState, setKeyState] = React.useState<boolean>(false);
    const onCustomRenderSubMenuChange = React.useCallback(() => {
        setKeyState(!keyState);
    }, [keyState]);
    const [isShowAllAssetsExpanded, toggleIsShowAllAssetsExpanded] = React.useState<boolean>(false);

    if (isBoardViewTypeSelected()) {
        config = getBoardCommandBarConfig(
            onCustomRenderSubMenuChange,
            isShowAllAssetsExpanded,
            toggleIsShowAllAssetsExpanded
        );
    } else {
        config = getSurfaceCommandBarConfig(onCustomRenderSubMenuChange);
    }

    const { primaryItems, farItems } = config;

    if (shouldShowHamburgerAndNewEventItems) {
        primaryItems.unshift(getHamburgerItem(), getNewEventItem());
    }

    if (isFeatureEnabled('mon-ribbon-calendar')) {
        farItems.push(getCommandingModeMenuItem());
    }

    const commandBarItems: FilterableMenuItem[] = [
        ...primaryItems,
        // this marks the split between the primary items and the farItems in the surface command bar
        ...farItems,
    ];
    commandBarItems.forEach((item, index, array) => {
        if (item) {
            item['aria-setsize'] = array.length;
            item['aria-posinset'] = index + 1; //posinset is 1 based
        }
    });

    const CalendarModuleCommandBarStyleProps = {
        root: {
            /*
             * this glocal css prop selects the nth element of the overflowSet and pushes all nth
             * elements to the right of the component, this effectively separates the primary and far items
             * of the surface command bar
             */
            selectors: {
                [`& .ms-OverflowSet-item:nth-child(${primaryItems.length})`]: {
                    display: 'flex',
                    flexGrow: 1,
                },
            },
            'padding-left': 0,
        },
    };

    const calendarSidePanelIsCollapsed = !getIsLeftNavShown();

    return (
        <FilterableCommandBar
            key={Number(keyState)}
            className={classNames(
                styles.commandBar,
                (calendarSidePanelIsCollapsed || shouldShowLeftNavAsOverlay()) &&
                    !isFeatureEnabled('tri-officeRail') &&
                    styles.calendarSidePanelIsHidden
            )}
            filterableItems={commandBarItems}
            elipisisAriaLabel={loc(commandBarEllipsisAriaLabel)}
            styles={CalendarModuleCommandBarStyleProps}
        />
    );
});
